import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Platform, View, StyleSheet } from "react-native";
import { colors, spacing } from "../../../../styles/design-system";
import { ensureMathLive } from "../../../../utils/mathLive";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "math-field": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & { ref?: React.Ref<MathFieldElement | null>; style?: React.CSSProperties },
        HTMLElement
      >;
    }
  }
}

const MATH_FIELD_FONT_SIZE = 20;

type MathFieldElement = HTMLElement & {
  value: string;
  menuItems: unknown[];
  mathVirtualKeyboardPolicy: string;
  executeCommand: (command: [string, string]) => void;
  focus: () => void;
  disabled?: boolean;
};

/** MathLive's focusable input is inside shadow DOM; set aria-label on it so axe passes. */
function setMathFieldSinkAccessibleName(el: MathFieldElement): void {
  const sink =
    el.shadowRoot?.querySelector(".ML__keyboard-sink") ?? el.shadowRoot?.querySelector('[part="keyboard-sink"]');
  if (sink) sink.setAttribute("aria-label", "Math input");
}

function injectMathFieldStyles(): void {
  if (typeof document === "undefined") return;
  const id = "mathlive-hide-chrome";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    math-field::part(virtual-keyboard-toggle) { display: none !important; }
    math-field::part(menu-toggle) { display: none !important; }
  `;
  document.head.appendChild(style);
}

const mathFieldStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 48,
  maxHeight: 128,
  fontSize: MATH_FIELD_FONT_SIZE,
  backgroundColor: "transparent",
  border: "none",
  outline: "none",
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontFamily: "Inter, sans-serif",
  color: colors.grey[900],
  display: "block",
  overflow: "auto",
};

export interface MathFieldInputHandle {
  getValue: () => string;
  setValue: (latex: string) => void;
  insertLatex: (latex: string) => void;
  focus: () => void;
}

interface MathFieldInputProps {
  onValueChange?: (latex: string) => void;
  onSubmit?: () => void;
  isDisabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
}

const MathFieldInput = forwardRef<MathFieldInputHandle, MathFieldInputProps>(
  ({ onValueChange, onSubmit, isDisabled = false, onFocus, onBlur }, ref) => {
    const mathFieldRef = useRef<MathFieldElement | null>(null);
    const [mathLiveLoaded, setMathLiveLoaded] = useState(false);

    const onValueChangeRef = useRef(onValueChange);
    const onSubmitRef = useRef(onSubmit);
    const onFocusRef = useRef(onFocus);
    const onBlurRef = useRef(onBlur);
    onValueChangeRef.current = onValueChange;
    onSubmitRef.current = onSubmit;
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;

    useEffect(() => {
      if (Platform.OS !== "web") return;
      injectMathFieldStyles();
      ensureMathLive().then(() => setMathLiveLoaded(true));
    }, []);

    useEffect(() => {
      const mf = mathFieldRef.current;
      if (!mf || !mathLiveLoaded) return;

      mf.menuItems = [];
      mf.mathVirtualKeyboardPolicy = "manual";
      mf.setAttribute("theme", "light");
      setMathFieldSinkAccessibleName(mf);
      const raf = requestAnimationFrame(() => setMathFieldSinkAccessibleName(mf));

      const handleInput = () => onValueChangeRef.current?.(mf.value ?? "");
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onSubmitRef.current?.();
        }
      };
      const handleFocus = () => onFocusRef.current?.();
      const handleBlur = () => onBlurRef.current?.();

      mf.addEventListener("input", handleInput);
      mf.addEventListener("keydown", handleKeyDown);
      mf.addEventListener("focus", handleFocus);
      mf.addEventListener("blur", handleBlur);

      return () => {
        cancelAnimationFrame(raf);
        mf.removeEventListener("input", handleInput);
        mf.removeEventListener("keydown", handleKeyDown);
        mf.removeEventListener("focus", handleFocus);
        mf.removeEventListener("blur", handleBlur);
      };
    }, [mathLiveLoaded]);

    useEffect(() => {
      const mf = mathFieldRef.current;
      if (!mf) return;
      if ("disabled" in mf) mf.disabled = isDisabled;
    }, [isDisabled]);

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => mathFieldRef.current?.value ?? "",
        setValue: (latex: string) => {
          const mf = mathFieldRef.current;
          if (mf) mf.value = latex;
        },
        insertLatex: (latex: string) => {
          const mf = mathFieldRef.current;
          if (!mf) return;
          mf.executeCommand(["insert", latex]);
          mf.focus?.();
        },
        focus: () => mathFieldRef.current?.focus?.(),
      }),
      [mathLiveLoaded]
    );

    if (Platform.OS !== "web") return null;

    return (
      <View style={styles.container}>
        {mathLiveLoaded ? <math-field ref={mathFieldRef} style={mathFieldStyle} aria-label="Math input" /> : null}
      </View>
    );
  }
);

MathFieldInput.displayName = "MathFieldInput";

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
});

export default MathFieldInput;
